import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { PhoneInput, SelectInput, TextInput, TextareaInput, RadioselectInput, LocalizedNameFields } from '@/components/inputs';
import { addPrefixToDataParams } from '@/utils/string';
import { Button, TabsButtons } from '@/components/buttons';
import { useForm } from '@formspree/react';
import CheckIcon from '@/assets/icons/heroIcons/CheckCircle.svg';
import { Link } from '@/components/links';
import { Text } from '@/components/typography';
import { LINKS } from '@/constants/links';
import { required, email, phone, pipeValidators } from '@/utils/validators';
import { logEvent } from '@/analytics/events';
import { FormApi, createForm } from 'final-form';
import { useIsSelectedLanguage } from '@/hooks/useIsSelectedLanguage';
type FormValues = {
  interest?: string;
  name: string;
  surname: string;
  email: string;
  phone: string;
  message?: string;
  _subject: string;
  _language: string;
};
export const ContactForm = ({
  initialInterest,
  variant
}: {
  initialInterest?: string;
  variant?: 'forEshops' | 'forDevelopers';
}) => {
  const {
    t
  } = useTranslation(['forEshops', 'core']);
  const [interestedInCooperation, setInterestedInCooperation] = useState(true);
  const [form] = useState<FormApi<FormValues, Partial<FormValues>>>(() => createForm({
    onSubmit: data => {
      logEvent('generate_lead', {
        action: 'B2B'
      });
      setTimeout(() => {
        handleSubmitForm(data);
      });
    }
  }));
  const isCsLanguage = useIsSelectedLanguage('cs');
  const isSkLanguage = useIsSelectedLanguage('sk');
  useEffect(() => {
    if (initialInterest) {
      setInterestedInCooperation(true);
    }
  }, [initialInterest]);
  const [state, handleSubmitForm] = useForm('mqkvvnkl');
  const isForEshop = variant === 'forEshops';
  if (state.succeeded) {
    return <div className="rounded-xl bg-white px-4 py-6 text-center lg:rounded-4xl lg:p-12">
				<CheckIcon className="mx-auto h-16 w-16 text-blue-500" />
				<h2 className="mt-8 mb-3 text-3xl font-bold">{t('core:forms.success.title')}</h2>
				<p>
					<Trans t={t} i18nKey="core:forms.success.text">
						<br />
					</Trans>
				</p>
			</div>;
  }
  function renderSubject() {
    if (!isForEshop) {
      return t('form.fields.subject.integration');
    }
    if (interestedInCooperation) {
      return t('form.fields.subject.interestedInCooperation');
    }
    return t('form.fields.subject.query');
  }

  // reset fields state to untouched them and hide validation messages
  const resetFieldsState = () => {
    form.resetFieldState('name');
    form.resetFieldState('surname');
    form.resetFieldState('email');
    form.resetFieldState('phone');
  };
  return <>
			{isForEshop && <div className="mx-auto mb-8 max-w-132">
					<TabsButtons id="contactFormTab" activeTab={interestedInCooperation ? 0 : 1} tabs={[{
        label: t('forEshops:form.fields.subject.interestedInCooperation'),
        onClick: () => {
          setInterestedInCooperation(true);
          resetFieldsState();
        },
        id: 0
      }, {
        label: t('forEshops:form.fields.subject.query'),
        onClick: () => {
          setInterestedInCooperation(false);
          resetFieldsState();
        },
        id: 1
      }]} />
				</div>}
			<div className="rounded-xl bg-white px-4 py-6 lg:rounded-4xl lg:p-12">
				<Form form={form} onSubmit={form.submit} initialValues={{
        _subject: renderSubject(),
        _language: 'cs'
      }}>
					{({
          handleSubmit
        }) => <form method="post" onSubmit={data_0 => handleSubmit(data_0)} {...addPrefixToDataParams({
          analyticsParam: 'forEshops',
          testId: 'forEshops',
          prefix: 'form'
        })}>
							<div className="mb-10 flex flex-col gap-8">
								{isCsLanguage && isForEshop && interestedInCooperation && <div className="flex flex-col gap-4">
										<p className="font-semibold">{t('form.category.product')}</p>
										<div>
											<Field name="interest" validate={required} initialValue={isForEshop && initialInterest && interestedInCooperation ? initialInterest : ''}>
												{fieldRenderProps => <SelectInput {...fieldRenderProps} label={t('form.fields.interest.title')} hideLabel options={[{
                    label: t('form.fields.interest.options.1'),
                    value: t('form.fields.interest.options.1')
                  }, {
                    label: t('form.fields.interest.options.2'),
                    value: t('form.fields.interest.options.2')
                  }, {
                    label: t('form.fields.interest.options.3'),
                    value: t('form.fields.interest.options.3')
                  }, {
                    label: t('form.fields.interest.options.4'),
                    value: t('form.fields.interest.options.4')
                  }]} />}
											</Field>
										</div>
									</div>}

								<div className="flex flex-col gap-4">
									<p className="font-semibold">{t('form.category.contact')}</p>
									<LocalizedNameFields nameField={{
                name: 'name',
                label: t('core:forms.fields.name'),
                validate: required
              }} surnameField={{
                name: 'surname',
                label: t('core:forms.fields.surname'),
                validate: required
              }} />

									<Field name="email" type="email" validate={pipeValidators(required, email)}>
										{fieldRenderProps_0 => <TextInput {...fieldRenderProps_0} label={t('core:forms.fields.email')} />}
									</Field>
									<Field name="phone" type="tel" validate={pipeValidators(required, phone)}>
										{fieldRenderProps_1 => <PhoneInput {...fieldRenderProps_1} label={t('core:forms.fields.phone')} />}
									</Field>
								</div>
								{isForEshop && interestedInCooperation && <div className="flex flex-col gap-4">
										<p className="font-semibold">{t('core:forms.source.title')}</p>
										<Field name="source" initialValue={t('core:forms.source.options.1')}>
											{fieldRenderProps_2 => <RadioselectInput variant="onRow" {...fieldRenderProps_2} options={isCsLanguage ? [t('core:forms.source.options.1'), t('core:forms.source.options.2'), t('core:forms.source.options.3'), t('core:forms.source.options.4'), t('core:forms.source.options.5'), t('core:forms.source.options.6'), t('core:forms.source.options.7'), t('core:forms.source.options.other')] : isSkLanguage ? [t('core:forms.source.options.1'), t('core:forms.source.options.2'), t('core:forms.source.options.5'), t('core:forms.source.options.6'), t('core:forms.source.options.other')] : [t('core:forms.source.options.1'), t('core:forms.source.options.5'), t('core:forms.source.options.6'), t('core:forms.source.options.other')]} />}
										</Field>
									</div>}
								<div className="flex flex-col gap-4">
									<p className="font-semibold">{t('form.category.message')}</p>
									<Field name="message">
										{fieldRenderProps_3 => <TextareaInput {...fieldRenderProps_3} label={t('core:forms.fields.message')} />}
									</Field>

									<Text variant="p4" className="text-center text-gray-600">
										<Trans t={t} i18nKey="form.text">
											<Link testId="informationMemorandum" analyticsParam="informationMemorandum" to={LINKS().informationMemorandum} />
										</Trans>
									</Text>
								</div>
							</div>

							<Button type="submit" analyticsParam="submitForm" testId="submitForm" variant="primary" align="center" size="lg" busy={state.submitting}>
								{t('core:forms.fields.submit')}
							</Button>
						</form>}
				</Form>
			</div>
		</>;
};